import { useId } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { UploadTeamAvatarForm } from "@/components/dialog/team/update-team-form/UploadTeamAvatarForm";
import { FormButton } from "@/components/form/FormButton";
import { FormFieldError, FormFieldHint, FormFieldLabel } from "@/components/form/FormField";
import { FormInput } from "@/components/form/FormInput";
import { FormTextarea } from "@/components/form/FormTextarea";
import { useForm } from "@/components/form/hooks/useForm";
import { useFormField } from "@/components/form/hooks/useFormField";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import type { TeamFormFields } from "@/models/api/TeamApi";
import type { ComponentPropsWithRef } from "react";

export const UpdateTeamForm = observer(function UpdateTeamForm() {
  const t = useTranslations();
  const id = useId();
  const { dialog } = useStore();
  const { updateTeamForm } = dialog.teamDialog;

  const { formProps, register, status, serverError, clearServerError } = useForm<TeamFormFields>(
    (_fields, e) => {
      e.preventDefault();
      return updateTeamForm.submit();
    },
  );
  const titleFormField = useFormField("title", updateTeamForm, register, {
    onChange: clearServerError,
  });
  const permalinkFormField = useFormField("permalink", updateTeamForm, register, {
    onChange: clearServerError,
  });
  const aboutFormField = useFormField("about", updateTeamForm, register, {
    onChange: clearServerError,
  });

  return (
    <form className={twMerge("flex flex-col")} noValidate {...formProps}>
      <UploadTeamAvatarForm team={updateTeamForm.team} className="mb-5 mt-1" />
      <div className="flex flex-col mb-5">
        <FormFieldLabel htmlFor={`${id}-title`}>
          {t("createTeamDialog.stepTitle.placeholder")}
        </FormFieldLabel>
        <FormInput
          id={`${id}-title`}
          placeholder={t("createTeamDialog.stepTitle.placeholder")}
          keepPlaceholderOnTop={false}
          type="text"
          className="dark:bg-gray-800"
          {...titleFormField.inputProps}
        />
        <FormFieldError message={titleFormField.errorMessage} />
      </div>
      <div className="flex flex-col mb-5">
        <FormFieldLabel htmlFor={`${id}-permalink`}>
          {t("createTeamDialog.stepPermalink.placeholder")}
        </FormFieldLabel>
        <FormInput
          id={`${id}-permalink`}
          placeholder={t("createTeamDialog.stepPermalink.placeholder")}
          keepPlaceholderOnTop={false}
          type="text"
          valuePrefix="@"
          className="dark:bg-gray-800"
          {...permalinkFormField.inputProps}
        />
        <FormFieldError message={permalinkFormField.errorMessage} />
        <FormFieldHint message={t("createTeamDialog.stepPermalink.description")} />
        <FormFieldError message={serverError} />
      </div>
      <div className="flex flex-col mb-5">
        <FormFieldLabel htmlFor={`${id}-about`}>
          {t("createTeamDialog.stepAbout.placeholder")}
        </FormFieldLabel>
        <FormTextarea
          id={`${id}-about`}
          placeholder={t("createTeamDialog.stepAbout.placeholder")}
          keepPlaceholderOnTop={false}
          className="dark:bg-gray-800"
          {...(aboutFormField.inputProps as ComponentPropsWithRef<"textarea">)}
        />
        <FormFieldError message={aboutFormField.errorMessage} />
        <FormFieldHint message={t("createTeamDialog.stepAbout.description")} />
        <FormFieldError message={serverError} />
      </div>
      <FormButton
        isBusy={status === "submitting"}
        variant="accent"
        size="sm"
        type="submit"
        className="w-full max-w-[150px] mr-auto sm:max-w-none"
      >
        {t("teams.updateForm.submitButton")}
      </FormButton>
    </form>
  );
});
